import React, { useEffect, useMemo, useRef, useState } from 'react';
import StatBox from '../ui/StatBox';
import { GameIntro, GameOver, GameState, isTypingKey, useSaveGameResult } from './GameShell';
import { playCorrectSound, playWrongSound, playMissSound, playPopSound, playGameOverSound, playSuccessSound } from '../../utils/audio';
import { api } from '../../utils/api';
import { User } from '../../types';
import { ScorePops, useScorePops } from '../ui/ScorePops';

const HEIGHT = 420;
const BASE_Y = HEIGHT - 48;
const SHIP_ICONS = ['👾', '🛸', '👽'];
const FALLBACK_WORDS = ['moon', 'star', 'comet', 'orbit', 'laser', 'alien', 'rocket', 'planet', 'galaxy', 'meteor', 'space', 'shield', 'nova', 'pilot', 'quasar'];

interface Ship { id: number; word: string; x: number; y: number; typed: number; speed: number; icon: string }
interface Flash { id: number; x: number; y: number; at: number }

const SpaceDefenderGame: React.FC<{ user?: User }> = ({ user }) => {
  const [gameState, setGameState] = useState<GameState>('ready');
  const [, setFrame] = useState(0);
  const [words, setWords] = useState<string[]>(FALLBACK_WORDS);
  const areaRef = useRef<HTMLDivElement>(null);
  const { pops, pop } = useScorePops();

  const g = useRef({
    ships: [] as Ship[], shots: [] as Flash[], booms: [] as Flash[],
    lockedId: null as number | null, score: 0, level: 1, shields: 3, combo: 0,
    destroyed: 0, correct: 0, mistakes: 0, spawnTimer: 0, nextId: 1,
  });

  useEffect(() => {
    api.get<string[]>('/api/v1/game/words?count=100')
      .then(list => {
        const clean = list.map(w => w.toLowerCase()).filter(w => /^[a-z]{3,10}$/.test(w));
        if (clean.length > 20) setWords(clean);
      })
      .catch(() => {});
  }, []);

  const stars = useMemo(() => Array.from({ length: 70 }, (_, i) => ({
    id: i, x: Math.random() * 100, y: Math.random() * 100, size: Math.random() < 0.15 ? 3 : 2, twinkle: Math.random() < 0.3,
  })), []);

  const startGame = () => {
    g.current = { ships: [], shots: [], booms: [], lockedId: null, score: 0, level: 1, shields: 3, combo: 0, destroyed: 0, correct: 0, mistakes: 0, spawnTimer: 1.5, nextId: 1 };
    setGameState('playing');
  };

  useSaveGameResult(user, 'space-defender', gameState, g.current.score);

  const pickWord = (s: typeof g.current) => {
    const maxLen = s.level <= 1 ? 5 : s.level <= 3 ? 7 : 10;
    const minLen = s.level <= 2 ? 3 : 4;
    const usedFirst = new Set(s.ships.map(sh => sh.word[0]));
    const pool = words.filter(w => w.length >= minLen && w.length <= maxLen);
    const source = pool.length ? pool : words;
    for (let i = 0; i < 25; i++) {
      const w = source[Math.floor(Math.random() * source.length)];
      if (!usedFirst.has(w[0])) return w;
    }
    return null; // every free starting letter is taken; skip this spawn
  };

  // Game loop
  useEffect(() => {
    if (gameState !== 'playing') return;
    let raf = 0;
    let last = performance.now();
    const loop = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const s = g.current;

      s.spawnTimer -= dt;
      const maxShips = 2 + s.level;
      if (s.spawnTimer <= 0 && s.ships.length < maxShips) {
        const word = pickWord(s);
        // Keep new ships clear of ones still near the top so labels never overlap.
        let x = 8 + Math.random() * 84;
        for (let i = 0; i < 12 && s.ships.some(sh => sh.y < 140 && Math.abs(sh.x - x) < 18); i++) x = 8 + Math.random() * 84;
        if (word) {
          s.ships.push({
            id: s.nextId++, word, x, y: -30, typed: 0,
            speed: 10 + s.level * 4 + Math.random() * 5, icon: SHIP_ICONS[Math.floor(Math.random() * SHIP_ICONS.length)],
          });
        }
        s.spawnTimer = Math.max(1.0, 4.0 - s.level * 0.35);
      }

      for (const ship of s.ships) ship.y += ship.speed * dt;
      const landed = s.ships.filter(sh => sh.y >= BASE_Y - 20);
      if (landed.length) {
        s.ships = s.ships.filter(sh => sh.y < BASE_Y - 20);
        for (const sh of landed) {
          if (s.lockedId === sh.id) s.lockedId = null;
          s.booms.push({ id: sh.id, x: sh.x, y: BASE_Y - 10, at: now });
        }
        s.shields -= landed.length;
        s.combo = 0;
        if (s.shields <= 0) {
          s.shields = 0;
          playGameOverSound();
          setGameState('over');
          return;
        }
        playMissSound();
      }
      s.shots = s.shots.filter(f => now - f.at < 120);
      s.booms = s.booms.filter(f => now - f.at < 450);
      setFrame(f => f + 1);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState, words]);

  // Typing
  useEffect(() => {
    if (gameState !== 'playing') return;
    const onKey = (e: KeyboardEvent) => {
      const s = g.current;
      if (e.key === 'Backspace') { e.preventDefault(); s.lockedId = null; return; }
      if (!isTypingKey(e)) return;
      e.preventDefault();
      const letter = e.key.toLowerCase();
      const now = performance.now();

      let ship = s.ships.find(sh => sh.id === s.lockedId);
      if (!ship) {
        ship = s.ships.filter(sh => sh.word[0] === letter).sort((a, b) => b.y - a.y)[0];
        if (ship) s.lockedId = ship.id;
      }
      if (!ship || ship.word[ship.typed] !== letter) {
        s.mistakes++;
        s.combo = 0;
        playWrongSound();
        return;
      }

      ship.typed++;
      s.correct++;
      s.shots.push({ id: now, x: ship.x, y: ship.y, at: now });
      if (ship.typed < ship.word.length) { playCorrectSound(); return; }

      // Destroyed
      s.ships = s.ships.filter(sh => sh.id !== ship!.id);
      s.lockedId = null;
      s.booms.push({ id: ship.id, x: ship.x, y: ship.y, at: now });
      s.combo++;
      s.destroyed++;
      const gained = ship.word.length * 10 + Math.min(s.combo, 10) * 5;
      s.score += gained;
      pop(`+${gained}`, ship.x, (ship.y / HEIGHT) * 100, '#fde047');
      if (s.destroyed % 8 === 0) { s.level++; playSuccessSound(); } else playPopSound();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [gameState, pop]);

  const s = g.current;
  const accuracy = s.correct + s.mistakes > 0 ? Math.round((s.correct / (s.correct + s.mistakes)) * 100) : 100;

  if (gameState === 'ready') {
    return (
      <GameIntro icon="🚀" title="Space Defender" skill="accuracy under pressure -- every key is a laser shot." accent="text-indigo-600" button="bg-gradient-to-r from-indigo-500 to-fuchsia-500" onStart={startGame}>
        Alien ships carry words toward your base. Type the first letter of a word to lock on, then finish the word to blast the ship. Wrong keys break your combo, and 3 ships reaching the base ends the game. Press Backspace to switch targets.
      </GameIntro>
    );
  }
  if (gameState === 'over') {
    return (
      <GameOver
        message={s.destroyed >= 20 ? 'Galaxy saved -- amazing aim!' : 'The aliens got through this time. Try again!'}
        stars={s.destroyed >= 20 ? 3 : s.destroyed >= 10 ? 2 : s.destroyed > 0 ? 1 : 0}
        stats={[{ label: 'Score', value: s.score }, { label: 'Wave', value: s.level }, { label: 'Ships', value: s.destroyed }, { label: 'Accuracy', value: `${accuracy}%` }]}
        button="bg-gradient-to-r from-indigo-500 to-fuchsia-500"
        onRestart={startGame}
      />
    );
  }

  const width = areaRef.current?.offsetWidth ?? 800;
  const locked = s.ships.find(sh => sh.id === s.lockedId);
  const cannonX = width / 2;
  const cannonY = HEIGHT - 22;
  const aim = locked ? Math.atan2((locked.x / 100) * width - cannonX, cannonY - locked.y) * (180 / Math.PI) : 0;

  return (
    <div className="w-full">
      <div className="flex flex-wrap justify-around items-center mb-4 gap-3 p-3 bg-white/60 rounded-2xl shadow-inner">
        <StatBox label="Score" value={s.score} />
        <StatBox label="Wave" value={s.level} />
        <StatBox label="Combo" value={s.combo} />
        <div className="flex flex-col items-center px-4 py-2 min-w-[80px] rounded-2xl bg-white shadow-md ring-2 ring-white/70">
          <span className="text-[11px] font-extrabold uppercase tracking-wider text-rose-500">Shields</span>
          <span className="text-2xl tracking-wider" aria-label={`${s.shields} shields left`}>
            {'🛡️'.repeat(s.shields)}<span className="opacity-20">{'🛡️'.repeat(3 - s.shields)}</span>
          </span>
        </div>
      </div>
      <div ref={areaRef} className="relative overflow-hidden rounded-2xl border-4 border-white shadow-inner bg-gradient-to-b from-indigo-900 via-purple-900 to-slate-900 select-none" style={{ height: HEIGHT }}>
        {stars.map(st => (
          <span key={st.id} className={`absolute rounded-full bg-white ${st.twinkle ? 'animate-pulse' : 'opacity-70'}`} style={{ left: `${st.x}%`, top: `${st.y}%`, width: st.size, height: st.size }} />
        ))}

        <span className="fun-float absolute top-6 right-[10%] text-5xl" aria-hidden="true">🪐</span>
        <span className="fun-float absolute top-1/3 left-[6%] text-3xl" style={{ animationDelay: '2s' }} aria-hidden="true">🌙</span>
        <span className="fun-float absolute top-[55%] right-[22%] text-2xl opacity-80" style={{ animationDelay: '3.3s' }} aria-hidden="true">☄️</span>
        <svg className="absolute inset-0 w-full h-full pointer-events-none">
          {s.shots.map(f => (
            <line key={f.id} x1={cannonX} y1={cannonY - 18} x2={(f.x / 100) * width} y2={f.y + 10} stroke="#fde047" strokeWidth={4} strokeLinecap="round" opacity={0.9} />
          ))}
          <rect x={0} y={BASE_Y + 8} width={width} height={HEIGHT - BASE_Y} fill="#22c55e" opacity={0.55} />
          <g transform={`translate(${cannonX} ${cannonY}) rotate(${aim})`}>
            <rect x={-6} y={-32} width={12} height={28} rx={5} fill="#fbbf24" stroke="#fff" strokeWidth={2} />
          </g>
          <circle cx={cannonX} cy={cannonY} r={18} fill="#ec4899" stroke="#fff" strokeWidth={3} />
        </svg>

        {s.ships.map(sh => {
          const isLocked = sh.id === s.lockedId;
          return (
            <div key={sh.id} className="absolute -translate-x-1/2 flex flex-col items-center" style={{ left: `${sh.x}%`, top: sh.y }}>
              <span className={`text-4xl leading-none ${isLocked ? 'drop-shadow-[0_0_8px_rgba(244,114,182,0.9)]' : ''}`} aria-hidden="true">{sh.icon}</span>
              <span className={`mt-1 px-3 py-0.5 rounded-full font-mono text-lg font-bold border-2 shadow-md transition-transform ${isLocked ? 'bg-pink-500 border-white scale-110' : 'bg-sky-500/80 border-white/60'}`}>
                <span className="text-yellow-200">{sh.word.slice(0, sh.typed)}</span>
                <span className="text-white">{sh.word.slice(sh.typed)}</span>
              </span>
            </div>
          );
        })}

        {s.booms.map(b => (
          <span key={`boom-${b.id}`} className="absolute -translate-x-1/2 text-4xl pointer-events-none sd-boom" style={{ left: `${b.x}%`, top: b.y }} aria-hidden="true">💥</span>
        ))}
        <style>{`
          @keyframes sd-boom { from { transform: translateX(-50%) scale(0.5); opacity: 1; } to { transform: translateX(-50%) scale(1.8); opacity: 0; } }
          .sd-boom { animation: sd-boom 0.45s ease-out forwards; }
        `}</style>
        <ScorePops pops={pops} />
      </div>
      <p className="text-center text-sm text-gray-500 mt-3">Just start typing -- no need to click anything.</p>
    </div>
  );
};

export default SpaceDefenderGame;
